import { PaginatedResponse, PaginationParams } from '@console/utils';
import { apiClient } from './client';

export interface TicketAppointment {
  id?: string;
  date: string;
  workedTime: number;
  debitedTime: number;
  isDoubleDebited: boolean;
  userId: string;
  user?: {
    id: string;
    name: string;
    email: string;
  };
}

export interface Ticket {
  id: string;
  number: string;
  title: string;
  date: string;
  workedTime: number;
  debitedTime: number;
  contractId: string;
  userId: string;
  remoteId?: string;
  createdAt: string;
  updatedAt: string;
  user: {
    id: string;
    name: string;
    email: string;
  };
  contract: {
    id: string;
    number: string;
    company: {
      id: string;
      name: string;
    };
  };
  ticketAppointments?: TicketAppointment[];
}

export interface CreateTicketAppointmentDto {
  date: string;
  workedTime: number;
  isDoubleDebited?: boolean;
  userId: string;
}

export interface CreateTicketDto {
  number: string;
  title: string;
  date: Date;
  contractId: string;
  userId: string;
  remoteId?: string;
  appointments?: CreateTicketAppointmentDto[];
}

export interface UpdateTicketDto {
  number?: string;
  title?: string;
  date?: Date;
  contractId?: string;
  userId?: string;
  remoteId?: string;
  appointments?: CreateTicketAppointmentDto[];
}

export interface TicketFilters extends PaginationParams {
  contractId?: string;
  userId?: string;
  companyId?: string;
  startDate?: string;
  endDate?: string;
  withHours?: boolean;
}

export const ticketsApi = {
  getAll: async (params?: TicketFilters): Promise<PaginatedResponse<Ticket>> => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.companyId) searchParams.append('companyId', params.companyId);
    if (params?.startDate) searchParams.append('startDate', params.startDate);
    if (params?.endDate) searchParams.append('endDate', params.endDate);
    if (params?.withHours !== undefined)
      searchParams.append('withHours', params.withHours.toString());

    const queryString = searchParams.toString();
    const response = await apiClient.get(`/tickets${queryString ? `?${queryString}` : ''}`);
    return response.data;
  },

  getById: async (id: string): Promise<Ticket> => {
    const response = await apiClient.get(`/tickets/${id}`);
    return response.data;
  },

  getByContract: async (
    contractId: string,
    params?: PaginationParams
  ): Promise<PaginatedResponse<Ticket>> => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());

    const queryString = searchParams.toString();
    const response = await apiClient.get(
      `/tickets/contract/${contractId}${queryString ? `?${queryString}` : ''}`
    );
    return response.data;
  },

  getByUser: async (
    userId: string,
    params?: PaginationParams
  ): Promise<PaginatedResponse<Ticket>> => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());

    const queryString = searchParams.toString();
    const response = await apiClient.get(
      `/tickets/user/${userId}${queryString ? `?${queryString}` : ''}`
    );
    return response.data;
  },

  getByDateRange: async (
    startDate: string,
    endDate: string,
    params?: PaginationParams
  ): Promise<PaginatedResponse<Ticket>> => {
    const searchParams = new URLSearchParams();
    searchParams.append('startDate', startDate);
    searchParams.append('endDate', endDate);
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());

    const queryString = searchParams.toString();
    const response = await apiClient.get(`/tickets/date-range?${queryString}`);
    return response.data;
  },

  create: async (data: CreateTicketDto): Promise<Ticket> => {
    const ticketData = {
      number: data.number,
      title: data.title,
      date: data.date.toISOString(),
      contractId: data.contractId,
      userId: data.userId,
      ...(data.remoteId && { remoteId: data.remoteId }),
      ...(data.appointments && {
        appointments: data.appointments.map((appointment) => ({
          ...appointment,
          date: appointment.date,
        })),
      }),
    };

    const response = await apiClient.post('/tickets', ticketData);
    return response.data;
  },

  update: async (id: string, data: UpdateTicketDto): Promise<Ticket> => {
    const updateData = {
      ...(data.number && { number: data.number }),
      ...(data.title && { title: data.title }),
      ...(data.date && { date: data.date.toISOString() }),
      ...(data.contractId && { contractId: data.contractId }),
      ...(data.userId && { userId: data.userId }),
      ...(data.remoteId && { remoteId: data.remoteId }),
      ...(data.appointments !== undefined && {
        appointments: data.appointments.map((appointment) => ({
          ...appointment,
          date: appointment.date,
        })),
      }),
    };

    const response = await apiClient.patch(`/tickets/${id}`, updateData);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/tickets/${id}`);
  },
};
